/**
 * Scopes picked in the AI connectors wizard, read back by the consent page in
 * the same browser so Allow already carries them. It only pre-checks boxes:
 * the user still sees and can change every scope at consent.
 */
const STORAGE_KEY = 'mcp-scope-preset'
const TTL_MS = 30 * 60 * 1000

type KeyValueStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
type Entry = { scopes: string[]; at: number }

const browserStorage = (): KeyValueStorage | null => {
  try {
    return typeof window === 'undefined' ? null : window.localStorage
  } catch {
    return null
  }
}

const entryKey = (organizationId: number, service: string) =>
  `${organizationId}:${service}`

const readAll = (storage: KeyValueStorage | null): Record<string, Entry> => {
  try {
    const parsed: unknown = JSON.parse(storage?.getItem(STORAGE_KEY) ?? '{}')

    return parsed && typeof parsed === 'object'
      ? (parsed as Record<string, Entry>)
      : {}
  } catch {
    return {}
  }
}

const writeAll = (
  storage: KeyValueStorage | null,
  entries: Record<string, Entry>
) => {
  try {
    storage?.setItem(STORAGE_KEY, JSON.stringify(entries))
  } catch {
    // Storage full or blocked: consent simply starts with everything checked.
  }
}

export const saveScopePreset = (
  organizationId: number,
  service: string,
  scopes: Iterable<string>,
  storage: KeyValueStorage | null = browserStorage(),
  now: number = Date.now()
) => {
  const entries = readAll(storage)
  entries[entryKey(organizationId, service)] = { scopes: [...scopes], at: now }
  writeAll(storage, entries)
}

export const readScopePreset = (
  organizationId: number | null,
  service: string,
  storage: KeyValueStorage | null = browserStorage(),
  now: number = Date.now()
): Set<string> | null => {
  if (organizationId === null) return null
  const entry = readAll(storage)[entryKey(organizationId, service)]
  const fresh =
    !!entry &&
    typeof entry.at === 'number' &&
    now - entry.at <= TTL_MS &&
    Array.isArray(entry.scopes)

  return fresh
    ? new Set(entry.scopes.filter((s) => typeof s === 'string'))
    : null
}

export const clearScopePreset = (
  organizationId: number,
  service: string,
  storage: KeyValueStorage | null = browserStorage()
) => {
  const entries = readAll(storage)
  delete entries[entryKey(organizationId, service)]
  writeAll(storage, entries)
}
